// features/projects/templates/continuum/predictions.js
// ═══════════════════════════════════════════════════════════
// CONTINUUM PREDICTIONS — calibration registry
//
// Lists every forward prediction the user has registered against
// findings. Groups by status: DUE (resolution date passed, still
// pending) -> PENDING (future) -> RESOLVED.
//
// For DUE rows: a Resolve button opens an inline form that posts
// to /api/continuum/predictions/resolve with status in
// resolved-true / resolved-false / inconclusive.
//
// Over time this becomes a calibration track record — the
// methodology's actual hit rate at each confidence tier.
//
// Session 4 of the Continuum-actionability extension.
// ═══════════════════════════════════════════════════════════

import { useState, useEffect, useCallback } from 'react';
import { C, FONT as F } from './tokens';
import { DYNAMICS } from '../../../../lib/continuum/simulation/methodology';

const DYN_BY_KEY = Object.fromEntries(DYNAMICS.map(d => [d.key, d]));

const STATUS_COLOR = {
  pending:          C.gold,
  due:              C.amber,
  'resolved-true':  C.sage,
  'resolved-false': C.terra,
  inconclusive:     C.dim,
};

const STATUS_LABEL = {
  pending:          'PENDING',
  due:              'DUE',
  'resolved-true':  'CONFIRMED',
  'resolved-false': 'REFUTED',
  inconclusive:     'INCONCLUSIVE',
};

const CONFIDENCE_COLOR = {
  'high':     C.sage,
  'med-high': C.gold,
  'med-low':  C.amber,
  'low':      C.terra,
};

export default function ContinuumPredictions({ workspaceId }) {
  const [predictions, setPredictions] = useState(null);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    if (!workspaceId) return;
    setError(null);
    try {
      const r = await fetch(`/api/continuum/predictions/list?workspace_id=${workspaceId}`);
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.error || `HTTP ${r.status}`);
      }
      const j = await r.json();
      setPredictions(j.predictions || []);
    } catch (err) {
      setError(err.message);
      setPredictions([]);
    }
  }, [workspaceId]);

  useEffect(() => { reload(); }, [reload]);

  const loading = predictions === null;
  const grouped = groupByStatus(predictions || []);

  return (
    <div style={{
      height: '100%', background: C.bg, color: C.text,
      fontFamily: F.mono, fontSize: 12,
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
    }}>
      <Header
        loading={loading}
        onReload={reload}
        counts={grouped.counts}
      />
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '18px 22px' }}>
        {loading ? (
          <Empty msg="Loading predictions…" />
        ) : error ? (
          <Empty msg={`Error: ${error}`} tone="error" />
        ) : (predictions || []).length === 0 ? (
          <Empty msg="No predictions registered yet. Open the Findings tab, expand a finding that survived Phase A, and click 'Log a forward prediction'." />
        ) : (
          <>
            <Calibration predictions={predictions} />
            {grouped.due.length > 0 && (
              <Section title={`Due (${grouped.due.length})`} accent={C.amber}>
                {grouped.due.map(p => (
                  <PredictionCard key={p.id} prediction={p} workspaceId={workspaceId} onResolved={reload} />
                ))}
              </Section>
            )}
            {grouped.pending.length > 0 && (
              <Section title={`Pending (${grouped.pending.length})`} accent={C.gold}>
                {grouped.pending.map(p => (
                  <PredictionCard key={p.id} prediction={p} workspaceId={workspaceId} onResolved={reload} />
                ))}
              </Section>
            )}
            {grouped.resolved.length > 0 && (
              <Section title={`Resolved (${grouped.resolved.length})`} accent={C.sage}>
                {grouped.resolved.map(p => (
                  <PredictionCard key={p.id} prediction={p} workspaceId={workspaceId} onResolved={reload} />
                ))}
              </Section>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Grouping ─────────────────────────────────────────────────
function groupByStatus(predictions) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const due = [];
  const pending = [];
  const resolved = [];

  for (const p of predictions) {
    if (p.status === 'pending') {
      const resDate = new Date(p.resolution_date);
      if (resDate <= today) due.push(p);
      else pending.push(p);
    } else {
      resolved.push(p);
    }
  }
  // resolved sorted by resolved_at desc (most recent first)
  resolved.sort((a, b) => new Date(b.resolved_at || 0) - new Date(a.resolved_at || 0));

  return {
    due,
    pending,
    resolved,
    counts: { due: due.length, pending: pending.length, resolved: resolved.length },
  };
}

// ─── Header ───────────────────────────────────────────────────
// Matches features/projects/templates/continuum/findings.js Header
// pattern: pill label + context line + Refresh button.
function Header({ loading, onReload, counts }) {
  const accent = C.sage;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', padding: '0 20px', height: 42,
      borderBottom: `1px solid ${C.b1}`, background: C.s1, flexShrink: 0, gap: 14,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        fontSize: 9, padding: '3px 10px', borderRadius: 4,
        background: 'rgba(122,158,130,.10)', border: '1px solid rgba(122,158,130,.25)',
        color: accent,
      }}>
        <div style={{ width: 5, height: 5, borderRadius: '50%', background: accent }} />
        Predictions — Calibration Registry
      </div>
      {counts && (
        <div style={{ fontSize: 9, color: C.dim }}>
          {counts.due} due · {counts.pending} pending · {counts.resolved} resolved
        </div>
      )}
      <div style={{ marginLeft: 'auto' }}>
        <button
          onClick={onReload}
          disabled={loading}
          style={{
            fontSize: 9, padding: '5px 14px', borderRadius: 4,
            background: 'transparent', border: `1px solid ${C.b2}`,
            color: C.static, cursor: loading ? 'not-allowed' : 'pointer',
            fontFamily: F.mono, letterSpacing: '0.06em',
          }}
        >
          {loading ? '⟳ Loading' : '↻ Refresh'}
        </button>
      </div>
    </div>
  );
}

// ─── Empty / loading state ────────────────────────────────────
function Empty({ msg, tone }) {
  return (
    <div style={{
      padding: '40px 20px', textAlign: 'center',
      color: tone === 'error' ? C.terra : C.dim,
      fontFamily: F.sans, fontSize: 13, lineHeight: 1.6,
      maxWidth: 600, margin: '40px auto',
    }}>
      {msg}
    </div>
  );
}

// ─── Section wrapper ──────────────────────────────────────────
function Section({ title, accent, children }) {
  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{
        fontFamily: F.mono, fontSize: 9, letterSpacing: '0.16em',
        color: accent, textTransform: 'uppercase', fontWeight: 600,
        marginBottom: 10, paddingBottom: 6, borderBottom: `1px solid ${C.b1}`,
      }}>
        ◆ {title}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {children}
      </div>
    </div>
  );
}

// ─── Calibration summary (counts only for now) ────────────────
// Once we have ~10+ resolved predictions per confidence tier we can
// show actual hit-rates. For now show a simple counts strip so the
// section earns its space without overclaiming statistical insight.
function Calibration({ predictions }) {
  const tiers = ['high', 'med-high', 'med-low', 'low'];
  const stats = tiers.map(t => {
    const inTier = predictions.filter(p => p.confidence_at_registration === t);
    const resolved = inTier.filter(p => p.status?.startsWith('resolved'));
    const confirmed = inTier.filter(p => p.status === 'resolved-true').length;
    return {
      tier: t,
      total: inTier.length,
      resolved: resolved.length,
      hitRate: resolved.length > 0 ? Math.round((confirmed / resolved.length) * 100) : null,
    };
  });

  const anyData = stats.some(s => s.total > 0);
  if (!anyData) return null;

  return (
    <div style={{
      marginBottom: 24, padding: '12px 14px',
      background: 'rgba(200,169,110,.04)',
      border: `1px solid ${C.b1}`, borderRadius: 5,
    }}>
      <div style={{
        fontFamily: F.mono, fontSize: 9, letterSpacing: '0.16em',
        color: C.dim, textTransform: 'uppercase', marginBottom: 10,
      }}>
        ◆ Calibration by confidence tier
      </div>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10,
      }}>
        {stats.map(s => (
          <div key={s.tier} style={{
            padding: '8px 10px',
            background: s.total > 0 ? `${CONFIDENCE_COLOR[s.tier]}10` : 'transparent',
            border: `1px solid ${s.total > 0 ? CONFIDENCE_COLOR[s.tier] + '40' : C.b1}`,
            borderRadius: 4,
          }}>
            <div style={{
              fontFamily: F.mono, fontSize: 8, letterSpacing: '0.12em',
              color: s.total > 0 ? CONFIDENCE_COLOR[s.tier] : C.dim,
              textTransform: 'uppercase', fontWeight: 600,
            }}>
              {s.tier}
            </div>
            <div style={{
              fontFamily: F.mono, fontSize: 18, color: C.text, marginTop: 4,
              fontWeight: 300,
            }}>
              {s.hitRate !== null ? `${s.hitRate}%` : '—'}
            </div>
            <div style={{
              fontSize: 9, color: C.dim, fontFamily: F.mono, marginTop: 2,
            }}>
              {s.resolved}/{s.total} resolved
            </div>
          </div>
        ))}
      </div>
      <div style={{
        marginTop: 8, fontSize: 9, color: C.dim, fontFamily: F.mono,
        fontStyle: 'italic',
      }}>
        Hit rate = resolved-true / total resolved within tier. Statistically meaningful at n ≥ 10.
      </div>
      <CalibrationTimeline predictions={predictions} />
    </div>
  );
}

// ─── Calibration timeline ─────────────────────────────────────
// Pure-SVG dot plot: x-axis = resolution_date, y-axis = confidence
// tier (high → low top→bottom). Each prediction is a dot colored
// by status (sage=confirmed, terra=refuted, dim=inconclusive,
// gold=pending, amber=due). Today is marked as a vertical line.
//
// Keeps the visual restrained — no axes labels other than tier
// names + today marker. The dots tell the story: clusters of sage
// in a high tier means the methodology is calibrated; clusters of
// terra means it's overclaiming. Reading is left-to-right
// (chronological) and top-to-bottom (high → low confidence).
function CalibrationTimeline({ predictions }) {
  const TIERS = ['high', 'med-high', 'med-low', 'low'];
  const today = new Date(); today.setHours(0, 0, 0, 0);

  // Window: from earliest resolution_date - 30 days to latest + 30 days
  const dates = predictions
    .map(p => new Date(p.resolution_date))
    .filter(d => !isNaN(d));
  if (dates.length === 0) return null;
  const tMin = new Date(Math.min(...dates) - 30 * 86400000);
  const tMax = new Date(Math.max(...dates) + 30 * 86400000);
  const span = tMax - tMin || 1;

  const W = 720, H = 140;
  const padL = 80, padR = 18, padT = 18, padB = 28;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;

  const xOf = d => padL + ((new Date(d) - tMin) / span) * innerW;
  const yOf = tier => padT + (TIERS.indexOf(tier) + 0.5) * (innerH / TIERS.length);
  const todayX = xOf(today);

  const dotColor = (p) => {
    if (p.status === 'resolved-true')  return C.sage;
    if (p.status === 'resolved-false') return C.terra;
    if (p.status === 'inconclusive')   return C.dim;
    const isDue = p.status === 'pending' && new Date(p.resolution_date) <= today;
    return isDue ? C.amber : C.gold;
  };

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{
        fontFamily: F.mono, fontSize: 8, letterSpacing: '0.14em',
        color: C.dim, textTransform: 'uppercase', marginBottom: 6,
      }}>
        Timeline · resolution dates by tier
      </div>
      <div style={{ overflowX: 'auto' }}>
        <svg width={W} height={H} style={{
          background: 'rgba(255,255,255,.02)',
          border: `1px solid ${C.b1}`, borderRadius: 4,
          display: 'block',
        }}>
          {/* Tier rows + labels */}
          {TIERS.map((t, i) => (
            <g key={t}>
              <line
                x1={padL} x2={W - padR}
                y1={yOf(t)} y2={yOf(t)}
                stroke={C.b1} strokeDasharray="2 4"
              />
              <text
                x={padL - 8} y={yOf(t) + 3}
                textAnchor="end"
                fontFamily="DM Mono, monospace"
                fontSize="9"
                fill={CONFIDENCE_COLOR[t]}
                style={{ letterSpacing: '0.06em', textTransform: 'uppercase' }}
              >
                {t}
              </text>
            </g>
          ))}
          {/* Today marker */}
          {todayX >= padL && todayX <= W - padR && (
            <g>
              <line
                x1={todayX} x2={todayX}
                y1={padT - 4} y2={H - padB + 4}
                stroke={C.gold} strokeWidth="1" strokeDasharray="3 3"
              />
              <text
                x={todayX} y={H - padB + 18}
                textAnchor="middle"
                fontFamily="DM Mono, monospace"
                fontSize="8"
                fill={C.gold}
                style={{ letterSpacing: '0.04em' }}
              >
                today
              </text>
            </g>
          )}
          {/* Date axis: tMin / tMax labels at bottom corners */}
          <text
            x={padL} y={H - padB + 18}
            fontFamily="DM Mono, monospace"
            fontSize="8"
            fill={C.dim}
          >
            {tMin.toISOString().slice(0, 7)}
          </text>
          <text
            x={W - padR} y={H - padB + 18}
            textAnchor="end"
            fontFamily="DM Mono, monospace"
            fontSize="8"
            fill={C.dim}
          >
            {tMax.toISOString().slice(0, 7)}
          </text>
          {/* Prediction dots */}
          {predictions.map(p => {
            const tier = p.confidence_at_registration;
            if (!tier || !TIERS.includes(tier)) return null;
            const cx = xOf(p.resolution_date);
            const cy = yOf(tier);
            const color = dotColor(p);
            return (
              <circle
                key={p.id}
                cx={cx} cy={cy} r="5"
                fill={color}
                fillOpacity={p.status?.startsWith('resolved') || p.status === 'inconclusive' ? 0.8 : 0.5}
                stroke={color}
                strokeWidth="1.5"
              >
                <title>{`${p.dynamic_key} · ${p.status} · resolves ${formatDate(p.resolution_date)}`}</title>
              </circle>
            );
          })}
        </svg>
      </div>
      {/* Legend */}
      <div style={{
        display: 'flex', gap: 14, flexWrap: 'wrap',
        marginTop: 6, fontFamily: F.mono, fontSize: 8,
        color: C.dim, letterSpacing: '0.04em',
      }}>
        <Legend color={C.gold}  label="pending" />
        <Legend color={C.amber} label="due" />
        <Legend color={C.sage}  label="confirmed" />
        <Legend color={C.terra} label="refuted" />
        <Legend color={C.dim}   label="inconclusive" />
      </div>
    </div>
  );
}

function Legend({ color, label }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      <span style={{
        width: 8, height: 8, borderRadius: '50%',
        background: color, display: 'inline-block',
      }} />
      {label}
    </span>
  );
}

// ─── Prediction card ──────────────────────────────────────────
function PredictionCard({ prediction, workspaceId, onResolved }) {
  const [resolveOpen, setResolveOpen] = useState(false);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const resDate = new Date(prediction.resolution_date);
  const isDue = prediction.status === 'pending' && resDate <= today;
  const displayStatus = isDue ? 'due' : prediction.status;

  const statusColor = STATUS_COLOR[displayStatus] || C.dim;
  const statusLabel = STATUS_LABEL[displayStatus] || displayStatus.toUpperCase();

  const dyn = DYN_BY_KEY[prediction.dynamic_key];
  const dynLabel = dyn?.label || prediction.dynamic_key || 'unknown dynamic';

  const confColor = CONFIDENCE_COLOR[prediction.confidence_at_registration] || C.dim;

  return (
    <div style={{
      padding: '12px 14px',
      background: 'rgba(255,255,255,.02)',
      border: `1px solid ${isDue ? statusColor + '60' : C.b1}`,
      borderRadius: 5,
    }}>
      {/* Top strip: status pill · dynamic · confidence · dates */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
        marginBottom: 10, fontFamily: F.mono, fontSize: 9,
      }}>
        <span style={{
          padding: '2px 8px', borderRadius: 3,
          border: `1px solid ${statusColor}60`,
          background: `${statusColor}15`, color: statusColor,
          letterSpacing: '0.08em', fontWeight: 600, textTransform: 'uppercase',
        }}>
          {statusLabel}
        </span>
        <span style={{ color: C.mid, letterSpacing: '0.04em' }}>
          {dynLabel}
        </span>
        {prediction.confidence_at_registration && (
          <span style={{
            padding: '1px 6px', borderRadius: 2,
            border: `1px solid ${confColor}40`,
            color: confColor, letterSpacing: '0.04em',
          }}>
            confidence: {prediction.confidence_at_registration}
          </span>
        )}
        <span style={{ marginLeft: 'auto', color: C.dim, letterSpacing: '0.04em' }}>
          {prediction.status?.startsWith('resolved') || prediction.status === 'inconclusive'
            ? `resolved ${formatDate(prediction.resolved_at)}`
            : `resolves ${formatDate(prediction.resolution_date)}`}
        </span>
      </div>

      {/* Claim */}
      <div style={{
        fontSize: 12, color: C.text, lineHeight: 1.5,
        fontFamily: F.sans, whiteSpace: 'pre-wrap',
        marginBottom: prediction.resolution_criteria ? 8 : 0,
      }}>
        {prediction.claim}
      </div>

      {/* Linked hypothesis (cross-domain testability indicator) */}
      {prediction.hypothesis && (
        <div style={{
          marginTop: 8, padding: '6px 10px',
          background: 'rgba(77,184,184,.06)',
          border: `1px solid rgba(77,184,184,.30)`, borderRadius: 3,
          fontSize: 10, fontFamily: F.mono, color: C.teal,
          letterSpacing: '0.04em',
          display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
        }}>
          <span>↳ Tests parallel hypothesis</span>
          <span style={{
            fontFamily: F.sans, fontSize: 11, color: C.mid,
            letterSpacing: 'normal', flex: 1, lineHeight: 1.4,
            overflow: 'hidden', textOverflow: 'ellipsis',
            display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical',
          }}>
            {prediction.hypothesis.eco_claim}
          </span>
        </div>
      )}

      {/* Resolution criteria */}
      {prediction.resolution_criteria && (
        <div style={{
          marginTop: 8, paddingTop: 8, borderTop: `1px dashed ${C.b1}`,
        }}>
          <div style={{
            fontFamily: F.mono, fontSize: 8, letterSpacing: '0.14em',
            color: C.dim, textTransform: 'uppercase', marginBottom: 4,
          }}>
            Resolution criteria
          </div>
          <div style={{
            fontSize: 11, color: C.mid, lineHeight: 1.5,
            fontFamily: F.sans, whiteSpace: 'pre-wrap',
          }}>
            {prediction.resolution_criteria}
          </div>
        </div>
      )}

      {/* Resolution note (after resolution) */}
      {prediction.resolution_note && (
        <div style={{
          marginTop: 8, padding: '8px 10px',
          background: `${statusColor}08`,
          border: `1px solid ${statusColor}30`, borderRadius: 4,
        }}>
          <div style={{
            fontFamily: F.mono, fontSize: 8, letterSpacing: '0.14em',
            color: statusColor, textTransform: 'uppercase', marginBottom: 4,
          }}>
            Resolution note
          </div>
          <div style={{
            fontSize: 11, color: C.mid, lineHeight: 1.5,
            fontFamily: F.sans, whiteSpace: 'pre-wrap',
          }}>
            {prediction.resolution_note}
          </div>
        </div>
      )}

      {/* Resolve button (only for DUE) */}
      {isDue && !resolveOpen && (
        <div style={{ marginTop: 10, display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={() => setResolveOpen(true)}
            style={{
              background: C.amber, border: `1px solid ${C.amber}`,
              borderRadius: 3, padding: '5px 14px',
              fontFamily: F.mono, fontSize: 10, letterSpacing: '0.06em',
              color: '#1a1a1a', textTransform: 'uppercase',
              cursor: 'pointer', fontWeight: 600,
            }}
          >
            Resolve →
          </button>
        </div>
      )}

      {isDue && resolveOpen && (
        <ResolveForm
          prediction={prediction}
          workspaceId={workspaceId}
          onCancel={() => setResolveOpen(false)}
          onResolved={() => {
            setResolveOpen(false);
            onResolved?.();
          }}
        />
      )}
    </div>
  );
}

// ─── Resolve form ─────────────────────────────────────────────
function ResolveForm({ prediction, workspaceId, onCancel, onResolved }) {
  const [chosenStatus, setChosenStatus] = useState(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    if (!chosenStatus || saving) return;
    setSaving(true);
    setError(null);
    try {
      const r = await fetch('/api/continuum/predictions/resolve', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          workspace_id: workspaceId,
          prediction_id: prediction.id,
          status: chosenStatus,
          resolution_note: note.trim() || null,
        }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.error || `HTTP ${r.status}`);
      }
      onResolved?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const choices = [
    { key: 'resolved-true',  label: 'Confirmed',    color: C.sage },
    { key: 'resolved-false', label: 'Refuted',      color: C.terra },
    { key: 'inconclusive',   label: 'Inconclusive', color: C.dim },
  ];

  return (
    <div style={{
      marginTop: 12, padding: '12px',
      background: 'rgba(200,149,106,.06)',
      border: `1px solid ${C.amber}40`, borderRadius: 4,
    }}>
      <div style={{
        fontFamily: F.mono, fontSize: 8, letterSpacing: '0.16em',
        color: C.amber, textTransform: 'uppercase', marginBottom: 10, fontWeight: 600,
      }}>
        ◆ Resolve prediction
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
        {choices.map(c => (
          <button
            key={c.key}
            type="button"
            onClick={() => setChosenStatus(c.key)}
            style={{
              flex: 1,
              padding: '8px 10px',
              background: chosenStatus === c.key ? `${c.color}20` : 'transparent',
              border: `1px solid ${chosenStatus === c.key ? c.color : C.b2}`,
              borderRadius: 3,
              color: chosenStatus === c.key ? c.color : C.mid,
              fontFamily: F.mono, fontSize: 10, letterSpacing: '0.06em',
              textTransform: 'uppercase', cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            {c.label}
          </button>
        ))}
      </div>

      <label style={{ display: 'block' }}>
        <span style={{
          display: 'block',
          fontFamily: F.mono, fontSize: 8, letterSpacing: '0.14em',
          color: C.dim, textTransform: 'uppercase', marginBottom: 4,
        }}>
          Resolution note (optional — what evidence resolved it?)
        </span>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          placeholder="What did you observe? Cite specific data, dates, sources."
          style={{
            width: '100%',
            background: 'rgba(255,255,255,.02)',
            border: `1px solid ${C.b1}`, borderRadius: 3,
            padding: '6px 8px', fontFamily: 'inherit', fontSize: 12,
            color: C.text, resize: 'vertical', outline: 'none',
            boxSizing: 'border-box',
          }}
        />
      </label>

      {error && (
        <div style={{
          marginTop: 8, padding: '6px 10px',
          background: 'rgba(196,80,80,.08)',
          border: '1px solid rgba(196,80,80,.40)',
          borderRadius: 3, fontSize: 11, color: C.terra, fontFamily: F.mono,
        }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          style={{
            background: 'transparent', border: `1px solid ${C.b1}`,
            borderRadius: 3, padding: '6px 14px',
            fontFamily: F.mono, fontSize: 10, letterSpacing: '0.06em',
            color: C.dim, textTransform: 'uppercase', cursor: 'pointer',
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={!chosenStatus || saving}
          style={{
            background: chosenStatus ? C.amber : C.b2,
            border: `1px solid ${chosenStatus ? C.amber : C.b2}`,
            borderRadius: 3, padding: '6px 14px',
            fontFamily: F.mono, fontSize: 10, letterSpacing: '0.06em',
            color: chosenStatus ? '#1a1a1a' : C.dim,
            textTransform: 'uppercase',
            cursor: !chosenStatus || saving ? 'not-allowed' : 'pointer',
            fontWeight: 600,
          }}
        >
          {saving ? 'Saving…' : 'Mark resolved'}
        </button>
      </div>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────
function formatDate(d) {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString();
  } catch {
    return String(d);
  }
}
